import { Carousel } from "@circleco/compass/components/Carousel";
import { IconButton } from "@circleco/compass/components/IconButton";
import { PageOverlay } from "@circleco/compass/components/PageOverlay";
import { Typography } from "@circleco/compass/components/Typography";
import { useEffect, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

import { AttachmentItem } from "./AttachmentItem";
import { canDownloadGalleryAttachment } from "./canDownloadGalleryAttachment";
import type { AttachmentGalleryProps } from "./types";

interface AttachmentGalleryComponentProps extends AttachmentGalleryProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const AttachmentGalleryComponent = ({
  attachments = [],
  startIndex = 0,
  open,
  onOpenChange,
}: AttachmentGalleryComponentProps) => {
  const carouselRef = useRef<HTMLDivElement>(null);
  const boundedStartIndex = Math.min(
    startIndex,
    Math.max(attachments.length - 1, 0),
  );
  const [index, setIndex] = useState(boundedStartIndex);
  const hasMultipleAttachments = attachments.length > 1;

  useEffect(() => {
    if (open && attachments.length > 0) {
      setIndex(boundedStartIndex);
    }
  }, [open, boundedStartIndex, attachments.length]);

  useEffect(() => {
    if (!open || !hasMultipleAttachments) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      carouselRef.current
        ?.querySelector<HTMLElement>('[role="region"]')
        ?.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(frameId);
  }, [open, hasMultipleAttachments, boundedStartIndex]);

  const currentAttachment = attachments[index] || attachments[0];
  if (!currentAttachment) {
    return null;
  }

  return (
    <PageOverlay open={open} onOpenChange={onOpenChange}>
      <div className="flex size-full flex-col">
        <div className="flex h-16 items-center justify-between border-b px-6 border-secondary">
          <Typography variant="heading-md">
            {currentAttachment.filename}
          </Typography>
          <div className="flex items-center gap-x-2">
            {canDownloadGalleryAttachment(currentAttachment) && (
              <IconButton
                aria-label={t("download")}
                icon="arrow-box-down"
                size="md"
                variant="ghost"
                renderLink={
                  <a
                    aria-label={t("download")}
                    href={currentAttachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={currentAttachment.filename}
                  />
                }
              />
            )}
            <IconButton
              aria-label={t("close")}
              icon="cross"
              size="md"
              onClick={() => onOpenChange(false)}
              variant="ghost"
            />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-between">
          {hasMultipleAttachments ? (
            <Carousel.Root
              ref={carouselRef}
              activeIndex={index}
              onActiveIndexChange={setIndex}
              aria-label={t("tiptap.attachments")}
              align="center"
            >
              {attachments.map((attachment, attachmentIndex) => (
                <Carousel.Slide
                  key={`${attachment.url}-${attachment.filename}`}
                >
                  <AttachmentItem
                    attachment={attachment}
                    isActive={attachmentIndex === index}
                  />
                </Carousel.Slide>
              ))}
            </Carousel.Root>
          ) : (
            <AttachmentItem attachment={currentAttachment} isActive />
          )}
        </div>
      </div>
    </PageOverlay>
  );
};
